import React, { Component } from 'react';
import { withAsyncActions } from 'react-async-client';
import { prop } from 'ramda';
import { Row, Col } from 'antd';
import Translate from 'react-translate-component';
import { withState } from 'recompose';

import { getMetroRequests, postMetroRequestApproval } from 'actions/asyncActions';
import { getStatusPayload, setFilter } from 'helpers/filterHelpers';
import { defaultMetroRequestFilters, defaultMetroRequestApprovalFilters } from 'constants/defaultFilters';
import { metroTown, metroRegion } from 'constants/dictionaries';
import { METRO_REQUEST_ROUTE_TYPES } from 'constants/metro';
import { fullAutoOptions } from 'constants/asyncClient';
import { TabsStatusFilter } from 'components/common/controls/TabsStatusFilter';
import { requestMetroApproval, requestMetro } from 'constants/routes';
import RefreshButton from 'components/common/controls/RefreshButton';
import StaticTable from 'components/common/tableComponents/StaticTable';
import { SidebarLink } from 'components/common/tableComponents/cellTemplates';
import { METRO_REQUEST_SIDEBAR } from 'constants/sidebarTypes';
import { withRouteOptions } from 'components/hocs/withRouteOptions';
import withTablePagination from 'components/common/tableComponents/wrappers/withTablePagination';
import TranslateField from 'components/common/fields/TranslateField';
import LoadField from 'components/common/fields/LoadField';

const TableComponent = withTablePagination(StaticTable);

@withState('line', 'setLine', 0)
@withAsyncActions({
    getMetroRequests: getMetroRequests
        .withPayload(props => ({
            line: props.line,
            linesPerPage: 20,
            ...(props.approval ? {
                userRole: 'approver',
                objtype: 'MetroRequestApproval',
                ...setFilter(
                    prop('status', getStatusPayload(props)),
                    defaultMetroRequestApprovalFilters
                )
            } : {
                userRole: 'initiator',
                objtype: 'MetroRequest',
                ...setFilter(
                    prop('status', getStatusPayload(props)),
                    defaultMetroRequestFilters
                )
            })
        }))
        .withOptions(fullAutoOptions),
    postMetroRequestApproval: postMetroRequestApproval
        .withSuccessHandler(({ getMetroRequests: { refresh } }) => refresh())
        .withErrorHandler(({ getMetroRequests: { refresh } }) => refresh())
})
export class MetroRequests extends Component {
    getTableColumns = (approval, params) => [
        {
            name: 'number',
            width: 35,
            additionalClass: 'table-cell-center',
            template: ({ rowIndex }) => rowIndex + 1,
        },
        {
            name: 'position',
            width: approval ? 250 : undefined,
            label: <Translate content="metro.request.positionTitle" />,
            template: ({ item }) =>
                <SidebarLink
                    componentName={METRO_REQUEST_SIDEBAR}
                    item={item}
                    text={(
                        <TranslateField
                            ru={item.position}
                            en={item.positionEngl}
                            defaultText='-'
                        />
                    )}
                    value={`${item.position || item.positionEngl || '-'}`}
                    params={{
                        approval,
                        hideButtons: approval && (params.status !== 'inwork'),
                    }}
                />
        },
        ...(approval ? [
            {
                name: 'routeType',
                width: 55,
                label: <Translate content="metro.request.requestRoute" />,
                template: ({ item }) => (
                    <Translate content={`metro.request.routes.${METRO_REQUEST_ROUTE_TYPES[item.routeType]}`} />
                ),
            },
            {
                name: 'region',
                label: <Translate content="metro.request.region" />,
                template: ({ item }) => (
                    <LoadField fieldValue='value' fieldName='name' loadName={metroRegion} fieldId={item.region} />
                ),
            },
            {
                name: 'city',
                width: 80,
                label: <Translate content="metro.request.city" />,
                template: ({ item }) => (
                    <LoadField fieldValue='value' fieldName='name' loadName={metroTown} fieldId={item.city} />
                ),
            },
            {
                name: 'storeCode',
                width: 45,
                label: <Translate content="metro.request.storeCode" />,
                template: ({ item }) => item.storeCode || '-',
            },
            {
                name: 'directorateEngl',
                width: 80,
                label: <Translate content="metro.request.directorateEngl" />,
                template: ({ item }) => item.directorateEngl || '-',
            },
            {
                name: 'unitEngl',
                width: 80,
                label: <Translate content="metro.request.unitEngl" />,
                template: ({ item }) => item.unitEngl || '-',
            },
            {
                name: 'divisionEngl',
                width: 80,
                label: <Translate content="metro.request.divisionEngl" />,
                template: ({ item }) => item.divisionEngl || '-',
            },
            {
                name: 'department',
                width: 200,
                label: <Translate content="metro.request.department" />,
                template: ({ item }) => (
                    <TranslateField
                        ru={item.department || '-'}
                        en={item.departmentEngl}
                    />
                ),
            },
            {
                name: 'initiator',
                width: 150,
                label: <Translate content="metro.request.initiatorName" />,
                template: ({ item }) => (
                    <TranslateField
                        ru={item.initiator || '-'}
                        en={item.initiatorEngl}
                    />
                )
            }
        ] : []),
        {
            name: 'status',
            width: 150,
            label: <Translate content="metro.requests.status" />,
            template: ({ item }) => <Translate content={`metro.requests.states.${item.state}`} />,
        }
    ];

    handlePagination = (page, pageSize) => {
        this.props.setLine((page - 1) * pageSize);
    }

    render() {
        const { getMetroRequests: { data, meta, refresh }, approval, match } = this.props;
        const { line, linesPerPage, linesTotal } = data;
        const pagination = { line, linesPerPage, linesTotal  };

        return <div>
            <Row className="react-table-controls list-controls">
                <Col span={18}>
                    { approval ? (
                        <TabsStatusFilter
                            defaultFilters={defaultMetroRequestApprovalFilters}
                            route={requestMetroApproval}
                            translate='metroApprovals'
                        />
                    ) : (
                        <TabsStatusFilter
                            defaultFilters={defaultMetroRequestFilters}
                            route={requestMetro}
                            translate='metro'
                        />
                    )}
                </Col>
                <Col span={6} className="text-right icon-bar">
                    <RefreshButton refreshMeta={meta} handleRefresh={refresh} />
                </Col>
            </Row>
            <div className='metro-table-wrapper'>
                <TableComponent
                    handlePagination={this.handlePagination}
                    pagination={pagination}
                    meta={meta}
                    refresh={refresh}
                    className='width-auto-table table-text-wrap'
                    columns={this.getTableColumns(approval, match.params)}
                    items={data.MetroRequest}
                />
            </div>
        </div>;
    }
}

MetroRequests.routeOptions = {
    title: 'menu.metroRequests',
};

export default withRouteOptions(MetroRequests);
